import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  buyersApi,
  type UpsertBuyerLeadBiographyPayload,
} from "@/api/buyers";
import { buyerKeys } from "./buyerKeys";

/**
 * The biography's two writes (PR 2d).
 *
 * There is no read hook: the block rides `BuyerLeadDetailDto.biography`, loaded
 * with the parent exactly as the qualification block is. A second read would be
 * a second thing to keep in step with the detail page for no gain.
 *
 * `analyse` looks like a pure read but can WRITE: on a buyer with no biography
 * yet, the server keeps the pasted text so a failed read or a walk-away does not
 * lose it. Once a biography exists it writes nothing, and the paste becomes the
 * source text only on Save.
 */
export function useAnalyseBuyerBiography(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rawText: string) => buyersApi.analyseBiography(buyerLeadId, rawText),
    // 🔴 SETTLED, not succeeded. A first paste is kept whether or not the read
    // works, and whether it was a first paste is the server's call — another
    // specialist may have saved a biography since this page loaded. So the
    // buyer's detail is refreshed after every Analyse, success or failure.
    //
    // It sits in the mutation's OWN options, not in a per-call callback: a
    // per-call callback does not run once its caller has unmounted — a
    // specialist can leave the page while a read is in flight — and the paste
    // modal deliberately ignores a read that lands after Cancel.
    onSettled: () => {
      void refetchDetailKeepingThePage(qc, buyerLeadId);
    },
  });
}

/** Saving IS the review — it stamps the reviewer, which is what makes the block
 *  package-eligible. */
export function useSaveBuyerBiography(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertBuyerLeadBiographyPayload) =>
      buyersApi.saveBiography(buyerLeadId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * The refresh after an Analyse, done so that it can never take the buyer's page
 * down.
 *
 * 🔴 A plain invalidation refetches through the page's own query, and a refetch
 * that fails puts that query into an error state with the old data still in
 * hand — which `BuyerDetailPage` answers by replacing the whole page, paste
 * modal and all. An Analyse that failed because the network dropped is exactly
 * when the refresh fails too, so the paste the error message said was kept
 * went with it.
 *
 * So this reads the buyer itself and writes only a success. A refresh that
 * fails marks the detail stale without refetching it, so the page keeps what it
 * has and the next read of the page fetches it again.
 *
 * A read of the buyer that lands in the cache while this one is in flight is
 * kept rather than overwritten. The case that matters is the Save after a quick
 * review, which is newer. The guard compares ARRIVAL, not age, and it only sees
 * reads that land DURING this one: a page fetch sent before this one wins if it
 * lands during it, and overwrites it outright if it lands after, since React
 * Query writes any fetch it did not cancel. Either way the older answer stays
 * until the detail is next refetched — up to the five-minute `staleTime`, or
 * the next invalidation. A narrow race, accepted and not closed here.
 */
async function refetchDetailKeepingThePage(qc: QueryClient, buyerLeadId: string) {
  const key = buyerKeys.detail(buyerLeadId);
  const before = qc.getQueryState(key)?.dataUpdateCount;
  try {
    const detail = await buyersApi.get(buyerLeadId);
    if (qc.getQueryState(key)?.dataUpdateCount !== before) return;
    qc.setQueryData(key, detail);
  } catch {
    void qc.invalidateQueries({ queryKey: key, refetchType: "none" });
  }
}
